// Mock data for NimQ TRACE — Text Recognition & Agent-driven Compliance Evaluation
// Supports any industry requiring document compliance analysis (PCI, healthcare, finance, etc.)

export type DocumentStatus = 'processing' | 'completed' | 'failed';
export type QualityRating = 'satisfactory' | 'needs_improvement' | 'unsatisfactory';
export type FindingSeverity = 'critical' | 'high' | 'medium' | 'low';
export type FindingCategory = 'data_mismatch' | 'signature_mismatch' | 'test_requirement' | 'formatting' | 'compliance_gap';
export type FeedbackStatus = 'open' | 'in_review' | 'resolved';

export interface Finding {
  id: string;
  category: FindingCategory;
  severity: FindingSeverity;
  section: string;
  title: string;
  description: string;
  aiComment: string;
  recommendation: string;
}

export interface AnalysisDocument {
  id: string;
  docName: string;
  fileName: string;
  fileType: 'PDF' | 'DOC' | 'DOCX';
  fileSize: string;
  templateVersion: string;
  templateFileName?: string;
  uploadDate: string;
  status: DocumentStatus;
  qualityRating?: QualityRating;
  issuesDetected?: number;
  findings?: Finding[];
  processingProgress?: number;
  analysisOverview?: string;
}

export interface FeedbackItem {
  id: string;
  documentId: string;
  type: 'edge_case' | 'policy_violation' | 'optimization';
  severity: FindingSeverity;
  title: string;
  description: string;
  status: FeedbackStatus;
  assignee: string;
  createdAt: string;
  proposedAction: string;
}

export interface DashboardMetric {
  label: string;
  value: string;
  change: number;
  trend: 'up' | 'down' | 'neutral';
}

// ── Uploaded Documents ──────────────────────────────────────────
export const uploadedDocuments: AnalysisDocument[] = [
  {
    id: 'doc-001',
    docName: 'Compliance Report v4.0.1',
    fileName: 'Compliance_Report_v4.0.1.pdf',
    fileType: 'PDF',
    fileSize: '2.4 MB',
    templateVersion: 'PCI SSS v1.2',
    uploadDate: '2026-02-17T09:30:00Z',
    status: 'completed',
    qualityRating: 'needs_improvement',
    issuesDetected: 11,
    processingProgress: 100,
    analysisOverview: 'The document shows multiple compliance gaps in PCI DSS requirements. 4 data mismatches identified across sections, 1 signature discrepancy, and 4 test requirement sections are not properly aligned.',
    findings: [
      {
        id: 'f-001',
        category: 'data_mismatch',
        severity: 'high',
        section: '3.2.1',
        title: 'PTS Device Reference Ambiguity',
        description: 'The assessor refers to PTS devices "VeriFone M400 and VeriFone M425/M450" as "PTS P2PE devices". Please clarify what is meant by "PTS P2PE devices."',
        aiComment: 'The term "PTS P2PE devices" conflates two separate PCI certifications. PTS devices are validated under the PIN Transaction Security standard, while P2PE is a separate validation program.',
        recommendation: 'Update device references to use correct PCI terminology. Clearly distinguish between PTS-validated devices and P2PE-validated solutions.',
      },
      {
        id: 'f-002',
        category: 'data_mismatch',
        severity: 'high',
        section: '3.2.1',
        title: 'Legacy Implementation Compatibility',
        description: 'Please provide additional details regarding which device does not list legacy implementation support between MX900 and the listed P2PE implementations.',
        aiComment: 'The assessment does not clearly differentiate between new deployment compatibility and legacy PTS device software versions.',
        recommendation: 'Include a compatibility matrix documenting which software versions work with which PTS devices.',
      },
      {
        id: 'f-003',
        category: 'data_mismatch',
        severity: 'medium',
        section: '2.4.2',
        title: 'P2PE / non-P2PE Terminology Inconsistency',
        description: 'Please clarify what is meant by the assessor\'s comment, "In both P2PE and non-P2PE devices".',
        aiComment: 'The phrasing introduces ambiguity about the scope of the assessment. It is unclear whether the controls apply universally or only to specific device categories.',
        recommendation: 'Revise the statement to clearly define the scope of controls for each device category.',
      },
      {
        id: 'f-004',
        category: 'data_mismatch',
        severity: 'medium',
        section: '1.1.g',
        title: 'Objective Control Scope Unclear',
        description: 'The Objective contains the relevant controls but the scope of the assessment is unclear regarding vendor configuration guidance.',
        aiComment: 'The objective states verification of vendor configuration but does not specify which vendor controls or OS-level configurations are in scope.',
        recommendation: 'Add explicit scoping statement for vendor configuration controls.',
      },
      {
        id: 'f-005',
        category: 'signature_mismatch',
        severity: 'critical',
        section: 'Page 21',
        title: 'Lead Assessor Signature Mismatch',
        description: 'Lead assessor signature is not matching or clear on page number 21.',
        aiComment: 'Signatures must be clearly legible and match registered assessor details. An unclear or mismatched signature can invalidate the entire document submission.',
        recommendation: 'Re-sign with a clear, verifiable signature matching the registered QSA details.',
      },
      {
        id: 'f-006',
        category: 'test_requirement',
        severity: 'high',
        section: '11.2',
        title: 'Missing Documentation References',
        description: 'There are documentation references in the report which are not included in the tables or this section.',
        aiComment: 'Evidence references (e.g., "Doc-R-2-21") are cited in the assessment but the referenced documents are not included in the submission.',
        recommendation: 'Include all referenced documentation as appendices. Cross-reference each evidence citation with the actual document.',
      },
      {
        id: 'f-007',
        category: 'test_requirement',
        severity: 'high',
        section: '11.2',
        title: 'Evidence Review Gaps',
        description: 'Please update to ensure all documentation and evidence reviewed during the assessment is included in this section.',
        aiComment: 'Multiple test requirement sections reference evidence that is not traceable within the document. This creates an audit trail gap.',
        recommendation: 'Create a comprehensive evidence index linking each test requirement to its supporting documentation.',
      },
      {
        id: 'f-008',
        category: 'test_requirement',
        severity: 'medium',
        section: '11.2',
        title: 'Test Procedure Completeness',
        description: 'Four test requirement sections are not in place. Please review and align them with the required structure.',
        aiComment: 'The missing test procedures create gaps in the validation chain, making it impossible to verify full compliance.',
        recommendation: 'Add the four missing test requirement sections with complete procedures, evidence, and findings.',
      },
      {
        id: 'f-009',
        category: 'test_requirement',
        severity: 'medium',
        section: '11.2',
        title: 'Assessment Scope Definition',
        description: 'The assessment scope definition references need to be updated per the defined assessment structure.',
        aiComment: 'Scope references are inconsistent between the executive summary and the detailed test sections.',
        recommendation: 'Harmonize scope definitions across all sections to maintain consistency.',
      },
      {
        id: 'f-010',
        category: 'formatting',
        severity: 'low',
        section: 'Multiple',
        title: 'Template Formatting Deviations',
        description: 'Several sections deviate from the standard template formatting requirements.',
        aiComment: 'Minor formatting issues detected but they do not impact the substantive compliance assessment.',
        recommendation: 'Review the compliance template guidelines and adjust formatting accordingly.',
      },
      {
        id: 'f-011',
        category: 'compliance_gap',
        severity: 'medium',
        section: '3.2.1',
        title: 'Vendor Delegation Compliance',
        description: 'The implementation notes do not appear to be approved for new deployments as part of the legacy compatibility assessment.',
        aiComment: 'Delegation of compliance responsibilities to vendors requires explicit documentation. The current statement is ambiguous about approval status.',
        recommendation: 'Clearly document vendor delegation terms and include approval evidence from the acquiring entity.',
      },
    ],
  },
  {
    id: 'doc-002',
    docName: 'Audit Summary v3.2.3',
    fileName: 'Audit_Summary_v3.2.3.pdf',
    fileType: 'PDF',
    fileSize: '1.8 MB',
    templateVersion: 'PCI SSS v1.1',
    uploadDate: '2026-02-17T08:15:00Z',
    status: 'completed',
    qualityRating: 'satisfactory',
    issuesDetected: 3,
    processingProgress: 100,
    analysisOverview: 'Overall quality meets acceptable standards. Minor formatting issues detected. No critical compliance gaps found.',
    findings: [
      {
        id: 'f-012',
        category: 'formatting',
        severity: 'low',
        section: '2.1',
        title: 'Table Header Alignment',
        description: 'Table headers in Section 2.1 do not align with the PCI template standard.',
        aiComment: 'Visual formatting issue only. Does not impact compliance validity.',
        recommendation: 'Adjust table formatting to match PCI template requirements.',
      },
      {
        id: 'f-013',
        category: 'data_mismatch',
        severity: 'medium',
        section: '4.3',
        title: 'Version Number Discrepancy',
        description: 'The software version listed in Section 4.3 differs from the executive summary.',
        aiComment: 'Version inconsistency may indicate an update was not reflected across all sections.',
        recommendation: 'Verify the correct software version and update all references consistently.',
      },
      {
        id: 'f-014',
        category: 'test_requirement',
        severity: 'low',
        section: '8.1',
        title: 'Test Date Formatting',
        description: 'Test dates in Section 8.1 use inconsistent date formats (MM/DD/YYYY vs YYYY-MM-DD).',
        aiComment: 'Inconsistent date formats can cause confusion during audit reviews.',
        recommendation: 'Standardize all dates to ISO 8601 format (YYYY-MM-DD).',
      },
    ],
  },
  {
    id: 'doc-003',
    docName: 'Security Assessment v5.0.2',
    fileName: 'Security_Assessment_v5.0.2.pdf',
    fileType: 'PDF',
    fileSize: '3.1 MB',
    templateVersion: 'PCI SSS v1.2',
    uploadDate: '2026-02-17T07:00:00Z',
    status: 'completed',
    qualityRating: 'unsatisfactory',
    issuesDetected: 18,
    processingProgress: 100,
    analysisOverview: 'Significant quality issues identified. Multiple critical compliance gaps and missing evidence. Substantial revisions required before resubmission.',
    findings: [],
  },
  {
    id: 'doc-004',
    docName: 'Validation Report v2.1.3',
    fileName: 'Validation_Report_v2.1.3.pdf',
    fileType: 'PDF',
    fileSize: '2.0 MB',
    templateVersion: 'PCI SSS v1.1',
    uploadDate: '2026-02-16T14:30:00Z',
    status: 'completed',
    qualityRating: 'satisfactory',
    issuesDetected: 2,
    processingProgress: 100,
    analysisOverview: 'Quality meets all required standards. Only minor observations noted.',
    findings: [],
  },
  {
    id: 'doc-005',
    docName: 'Compliance Review v4.2.2',
    fileName: 'Compliance_Review_v4.2.2.pdf',
    fileType: 'PDF',
    fileSize: '958 KB',
    templateVersion: 'PCI SSS v1.2',
    uploadDate: '2026-02-16T10:00:00Z',
    status: 'completed',
    qualityRating: 'needs_improvement',
    issuesDetected: 7,
    processingProgress: 100,
    analysisOverview: 'Several areas require improvement including evidence traceability and test procedure completeness.',
    findings: [],
  },
];

// ── Dashboard Analytics ─────────────────────────────────────────
export const weeklyStats = [
  { day: 'Mon', uploads: 12, issues: 34, resolved: 28 },
  { day: 'Tue', uploads: 15, issues: 42, resolved: 38 },
  { day: 'Wed', uploads: 8, issues: 21, resolved: 19 },
  { day: 'Thu', uploads: 18, issues: 56, resolved: 45 },
  { day: 'Fri', uploads: 14, issues: 38, resolved: 35 },
  { day: 'Sat', uploads: 5, issues: 12, resolved: 11 },
  { day: 'Sun', uploads: 3, issues: 8, resolved: 7 },
];

export const qualityDistribution = [
  { name: 'Satisfactory', value: 42, color: '#22c55e' },
  { name: 'Needs Improvement', value: 31, color: '#f59e0b' },
  { name: 'Unsatisfactory', value: 12, color: '#ef4444' },
];

export const issuesByCategory = [
  { category: 'Data Mismatch', count: 45, percentage: 32 },
  { category: 'Test Requirements', count: 38, percentage: 27 },
  { category: 'Compliance Gap', count: 28, percentage: 20 },
  { category: 'Signature Issues', count: 15, percentage: 11 },
  { category: 'Formatting', count: 14, percentage: 10 },
];

export const processingTimeline = [
  { time: '6AM', docs: 2, avgTime: 45 },
  { time: '8AM', docs: 5, avgTime: 52 },
  { time: '10AM', docs: 8, avgTime: 48 },
  { time: '12PM', docs: 12, avgTime: 55 },
  { time: '2PM', docs: 10, avgTime: 50 },
  { time: '4PM', docs: 7, avgTime: 47 },
  { time: '6PM', docs: 4, avgTime: 42 },
  { time: '8PM', docs: 2, avgTime: 38 },
];

// ── Feedback Items ──────────────────────────────────────────────
export const feedbackItems: FeedbackItem[] = [
  {
    id: 'fb-001',
    documentId: 'doc-001',
    type: 'policy_violation',
    severity: 'critical',
    title: 'Signature verification needs AI model update',
    description: 'Current model flags unclear signatures but cannot verify against registered QSA database. Manual verification still required for all signature checks.',
    status: 'open',
    assignee: 'Sarah Chen',
    createdAt: '2026-02-17T10:00:00Z',
    proposedAction: 'Integrate QSA registry API for automated signature verification against the registered assessor database.',
  },
  {
    id: 'fb-002',
    documentId: 'doc-001',
    type: 'edge_case',
    severity: 'high',
    title: 'PTS vs P2PE terminology detection improvement',
    description: 'AI model sometimes conflates PTS and P2PE certification types. The distinction is critical for PCI compliance assessments.',
    status: 'in_review',
    assignee: 'Marcus Wong',
    createdAt: '2026-02-17T09:30:00Z',
    proposedAction: 'Fine-tune the compliance model with additional PCI certification taxonomy training data to distinguish between PTS, P2PE, and PA-DSS certifications.',
  },
  {
    id: 'fb-003',
    documentId: 'doc-002',
    type: 'optimization',
    severity: 'low',
    title: 'Reduce false positives on formatting checks',
    description: 'Minor formatting variations are being flagged unnecessarily. The model should distinguish between substantive formatting issues and cosmetic differences.',
    status: 'resolved',
    assignee: 'Alex Rivera',
    createdAt: '2026-02-16T14:00:00Z',
    proposedAction: 'Adjust formatting check thresholds to focus on structural compliance issues rather than cosmetic variations.',
  },
  {
    id: 'fb-004',
    documentId: 'doc-003',
    type: 'policy_violation',
    severity: 'high',
    title: 'Missing evidence cross-reference detection',
    description: 'The model detected missing documentation references but did not flag the severity accurately. Some missing references were critical audit trail gaps.',
    status: 'open',
    assignee: 'Sarah Chen',
    createdAt: '2026-02-16T11:00:00Z',
    proposedAction: 'Implement weighted severity scoring for evidence references based on the criticality of the associated test requirement.',
  },
];

// ── History for sidebar ─────────────────────────────────────────
export const searchHistory = {
  today: [
    { docName: 'Compliance Report v4.0.1', label: 'Compliance Report v4.0.1' },
    { docName: 'Audit Summary v3.2.3', label: 'Audit Summary v3.2.3' },
    { docName: 'Security Assessment v5.0.2', label: 'Security Assessment v5.0.2' },
  ],
  yesterday: [
    { docName: 'Validation Report v2.1.3', label: 'Validation Report v2.1.3' },
    { docName: 'Compliance Review v4.2.2', label: 'Compliance Review v4.2.2' },
  ],
};

// ── Notifications ───────────────────────────────────────────────
export const initialNotifications = [
  {
    id: 'n-001',
    type: 'critical' as const,
    title: 'Critical: Signature Mismatch',
    message: 'Compliance Report v4.0.1 has a critical signature mismatch on page 21.',
    timestamp: '2026-02-17T09:45:00Z',
    read: false,
    documentId: 'doc-001',
  },
  {
    id: 'n-002',
    type: 'warning' as const,
    title: 'Analysis Complete',
    message: 'Security Assessment v5.0.2 analysis complete. 18 issues detected. Quality: Unsatisfactory.',
    timestamp: '2026-02-17T07:30:00Z',
    read: false,
    documentId: 'doc-003',
  },
  {
    id: 'n-003',
    type: 'success' as const,
    title: 'Quality Verified',
    message: 'Audit Summary v3.2.3 passed quality checks. Rating: Satisfactory.',
    timestamp: '2026-02-17T08:45:00Z',
    read: true,
    documentId: 'doc-002',
  },
  {
    id: 'n-004',
    type: 'info' as const,
    title: 'Weekly Report Ready',
    message: 'Your compliance analysis report for Feb 10-16 is available for download.',
    timestamp: '2026-02-16T08:00:00Z',
    read: true,
  },
];

// ── Quality Rating Descriptions ─────────────────────────────────
export const qualityRatingDescriptions = {
  satisfactory: {
    label: 'Satisfactory',
    description: 'The overall level of quality is acceptable, although there may still be areas where quality in reporting can be improved upon. No changes are required.',
    color: 'green',
  },
  needs_improvement: {
    label: 'Needs Improvement',
    description: 'The overall quality was determined to be below the acceptable level. Improvements MUST be made before AQM accepts the submission.',
    color: 'amber',
  },
  unsatisfactory: {
    label: 'Unsatisfactory',
    description: 'The overall level of quality has been determined to be insufficient. Improvements MUST be made before AQM accepts the submission. Repeated instances may result in remediation and/or revocation.',
    color: 'red',
  },
};

// ── Template versions ───────────────────────────────────────────
export const templateVersions = [
  { value: 'auto-detect', label: 'Auto-detect from uploaded template' },
  { value: 'custom', label: 'Custom uploaded template' },
];
